import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { cognitoService } from '../../services/cognito.service';
import { CognitoUserSession } from 'amazon-cognito-identity-js';

export interface AuthState {
  isLogged: boolean | null;
  isLoading: boolean;
  error: string | null;
  user: any | null;
  session: CognitoUserSession | null;
  challengeName?: string;
  challengeParams?: any;
  companyselected: any | null;
}

const initialState: AuthState = {
  isLogged: null,
  isLoading: false,
  error: null,
  user: null,
  session: null,
  companyselected: null,
};

export const loadAuthData = createAsyncThunk('auth/loadAuthData', async (_, { rejectWithValue }) => {
  try {
    const session = await cognitoService.getSession();
    const user = await cognitoService.getUserAttributes();
    return { session, user };
  } catch (error) {
    return rejectWithValue(null);
  }
});

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: { UserName: string; Password: string }, { rejectWithValue }) => {
    try {
      const session = await cognitoService.signIn(credentials.UserName, credentials.Password);
      const user = await cognitoService.getUserAttributes();
      return { session, user };
    } catch (error: any) {
      if (error.code === 'NewPasswordRequired') {
        return rejectWithValue({
          challenge: 'NEW_PASSWORD_REQUIRED',
          userAttributes: error.userAttributes,
          message: error.message,
        });
      }
      return rejectWithValue(error.message);
    }
  }
);

export const completeNewPassword = createAsyncThunk(
  'auth/completeNewPassword',
  async ({ newPassword, userAttributes }: { newPassword: string; userAttributes: any }, { rejectWithValue }) => {
    try {
      const session = await cognitoService.completeNewPassword(newPassword, userAttributes);
      const user = await cognitoService.getUserAttributes();
      return { session, user };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }, { rejectWithValue }) => {
    try {
      const result = await cognitoService.changePassword(oldPassword, newPassword);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      await cognitoService.forgotPassword(email);
      return email;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const confirmForgotPassword = createAsyncThunk(
  'auth/confirmForgotPassword',
  async ({ email, code, newPassword }: { email: string; code: string; newPassword: string }, { rejectWithValue }) => {
    try {
      await cognitoService.confirmPassword(email, code, newPassword);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  cognitoService.signOut();
  localStorage.removeItem('cognitoSession');
  return null;
});

export const refreshToken = createAsyncThunk('auth/refreshToken', async (token: string) => {
  return { Token: token };
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearAuthError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAuthData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadAuthData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLogged = true;
        state.session = action.payload?.session || null;
        state.user = action.payload?.user || null;
        state.error = null;
      })
      .addCase(loadAuthData.rejected, (state) => {
        state.isLoading = false;
        state.isLogged = false;
        state.session = null;
        state.user = null;
      })

      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLogged = true;
        state.session = action.payload.session;
        state.user = action.payload.user;
        state.challengeName = undefined;
        state.challengeParams = undefined;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action: any) => {
        state.isLoading = false;
        state.isLogged = false;

        if (action.payload?.challenge === 'NEW_PASSWORD_REQUIRED') {
          state.challengeName = 'NEW_PASSWORD_REQUIRED';
          state.challengeParams = {
            userAttributes: action.payload.userAttributes
          };
          state.error = null;
        } else {
          state.error = action.payload || 'Error al iniciar sesión';
        }
      })

      .addCase(completeNewPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(completeNewPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLogged = true;
        state.session = action.payload.session;
        state.user = action.payload.user;
        state.challengeName = undefined;
        state.challengeParams = undefined;
        state.error = null;
      })
      .addCase(completeNewPassword.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(confirmForgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmForgotPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(confirmForgotPassword.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(logout.fulfilled, (state) => {
        state.isLogged = false;
        state.user = null;
        state.session = null;
        state.error = null;
        state.challengeName = undefined;
        state.challengeParams = undefined;
        state.companyselected = null;
      })

      .addCase(refreshToken.fulfilled, (state, action) => {
        state.companyselected = action.payload;
      });
  },
});

export const { setAuthError, clearAuthError } = authSlice.actions;
export default authSlice.reducer;